import { memo, useCallback, useState } from 'react';
import { useInView } from 'react-intersection-observer';

import { Plus } from '@air/next-icons';
import { Button } from '@air/primitive-button';
import { useAirModal } from '@air/provider-modal';

import { CreateBoardModal } from '~/components/Modals/CreateBoardModal';
import { WorkspaceSidebarFooterActivityButton } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarFooterActivityButton';
import { WorkspaceSidebarFooterCreditUsageMeter } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarFooterCreditUsageMeter';
import { useWorkspaceNavPushMode } from '~/components/v2/hooks/useWorkspaceNavPushMode';
import { useWorkspaceSidebarStore } from '~/components/v2/store/features/workspace/useWorkspaceSidebarStore';
import { useCreditStats } from '~/swr-hooks/workspaces/useCreditStats';

export const WorkspaceSidebarFooter = memo(() => {
  const { isWorkspaceNavPushMode } = useWorkspaceNavPushMode();
  const isSidebarOpen = useWorkspaceSidebarStore((state) => state.open);
  const [isMeterHovered, setIsMeterHovered] = useState(false);
  const { data: creditStats } = useCreditStats();
  const [showCreateBoardModal] = useAirModal(CreateBoardModal);
  const showCreateBoard = !creditStats || creditStats.allocated == null || creditStats.remaining == null;

  const handleMouseEnter = useCallback(() => setIsMeterHovered(true), []);
  const handleMouseLeave = useCallback(() => setIsMeterHovered(false), []);
  const handleCreateBoard = useCallback(
    () => showCreateBoardModal({ trackLocation: 'side-nav-footer' }),
    [showCreateBoardModal],
  );

  const { inView, ref: inViewRef } = useInView({
    threshold: 1,
  });

  const footerRef = useCallback(
    (el: HTMLElement | null) => {
      inViewRef(el);
    },
    [inViewRef],
  );

  return (
    <footer
      className="sticky -bottom-px shrink-0 border-t border-t-transparent bg-inherit data-[sticky=true]:border-t-grey-5 data-[sticky=true]:pb-px"
      data-testid="WORKSPACE_SIDEBAR_FOOTER"
      data-sticky={!inView}
      ref={footerRef}
    >
      <div className="flex items-end gap-2 px-2 pb-1">
        {/* biome-ignore lint/a11y/noStaticElementInteractions: hover-only for layout, not primary interaction */}
        <div
          className="min-w-0 flex-1 rounded-8 transition-colors duration-200"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {showCreateBoard ? (
            <Button appearance="ghost" onClick={handleCreateBoard} prefix={<Plus className="size-4" />} color="grey">
              Create new board
            </Button>
          ) : (
            <WorkspaceSidebarFooterCreditUsageMeter />
          )}
        </div>
        {(!isMeterHovered || showCreateBoard) && !isWorkspaceNavPushMode && isSidebarOpen && (
          <div className="mb-1">
            <WorkspaceSidebarFooterActivityButton />
          </div>
        )}
      </div>
    </footer>
  );
});

WorkspaceSidebarFooter.displayName = 'WorkspaceSidebarFooter';
